import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentTermsDto } from '@hrms/shared';
import { get, post } from '@/lib/api-client';
import { useAuth } from '@/features/auth/use-auth';

export const legalKeys = {
  terms: ['legal', 'terms'] as const,
};

/**
 * The current terms and whether this person has accepted them.
 *
 * Reachable while the gate is blocking everything else — the endpoint carries
 * `@AllowTermsPending()`, because it is what somebody needs in order to get
 * past it.
 */
export function useTerms() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: legalKeys.terms,
    queryFn: () => get<CurrentTermsDto>('/legal/terms'),
    enabled: isAuthenticated,
    // Long, because publishing new terms is a deliberate act and not something
    // to poll for. A refresh or a re-login picks up a new version.
    staleTime: 10 * 60_000,
  });
}

/**
 * Whether the app should be blocked right now.
 *
 * Three conditions, all required: the gate is on, terms exist to read, and this
 * person has not accepted this version. While the query is still loading the
 * answer is "no" — a blank redirect on every cold start would be worse than a
 * moment of unblocked app, and the server refuses everything anyway.
 */
export function useTermsGate(): { blocked: boolean } {
  const { data } = useTerms();

  return {
    blocked: Boolean(data && data.requireAcceptance && data.body.trim() && !data.accepted),
  };
}

export function useAcceptTerms() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (version: string) => post<CurrentTermsDto>('/legal/terms/accept', { version }),
    onSuccess: () => {
      // Everything was refused while the gate was closed, so the caches behind
      // it hold 403s rather than data. Clearing the lot is cheaper than
      // reasoning about which ones.
      void queryClient.invalidateQueries();
    },
  });
}
